// ── Branded loading screen — animated FL calibration mark ──

import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useRef } from "react";

interface LoadingScreenProps {
  message?: string;
  messages?: string[];
  /** Show elapsed timer after this many ms (default 8000) */
  showTimerAfter?: number;
  /** Fill parent container instead of fixed overlay */
  inline?: boolean;
}

const DEFAULT_MESSAGES = [
  "Connecting to inspection database…",
  "Resolving field schema…",
  "Loading inspection records…",
  "Building grid columns…",
  "Preparing visualisations…",
];

function formatElapsed(ms: number): string {
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

export default function LoadingScreen({
  message,
  messages = DEFAULT_MESSAGES,
  showTimerAfter = 8000,
  inline = false,
}: LoadingScreenProps) {
  const [msgIndex, setMsgIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(Date.now());

  // Cycle messages every 3s
  useEffect(() => {
    if (message) return; // static message, skip cycling
    const interval = setInterval(() => {
      setMsgIndex((i) => (i + 1) % messages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [message, messages]);

  // Track elapsed time
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Date.now() - startRef.current);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const displayMessage = message ?? messages[msgIndex];

  const containerClass = inline
    ? "flex flex-col items-center justify-center h-full w-full min-h-[300px]"
    : "fixed inset-0 z-50 flex flex-col items-center justify-center bg-white/90 backdrop-blur-md";

  return (
    <div className={containerClass}>
      {/* Orbital animation container */}
      <div className="relative w-40 h-40 mb-8">
        {/* Outer ring — counter-clockwise */}
        <motion.svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 160 160"
          animate={{ rotate: -360 }}
          transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
        >
          <circle
            cx="80"
            cy="80"
            r="72"
            fill="none"
            stroke="#1f3a32"
            strokeWidth="3"
            strokeDasharray="8 4"
            opacity="0.3"
          />
          <circle
            cx="80"
            cy="80"
            r="72"
            fill="none"
            stroke="#1f3a32"
            strokeWidth="3"
            strokeDasharray="120 333"
            strokeLinecap="round"
            opacity="0.9"
          />
        </motion.svg>

        {/* Inner ring — clockwise */}
        <motion.svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 160 160"
          animate={{ rotate: 360 }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
        >
          <circle
            cx="80"
            cy="80"
            r="50"
            fill="none"
            stroke="#2a5a4a"
            strokeWidth="2.5"
            strokeDasharray="6 3"
            opacity="0.25"
          />
          <circle
            cx="80"
            cy="80"
            r="50"
            fill="none"
            stroke="#2a5a4a"
            strokeWidth="2.5"
            strokeDasharray="80 234"
            strokeLinecap="round"
            opacity="0.85"
          />
        </motion.svg>

        {/* Scanning sweep — outer */}
        <motion.svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 160 160"
          animate={{ rotate: 360 }}
          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
        >
          <defs>
            <linearGradient id="sweep-grad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#a4ca73" stopOpacity="0" />
              <stop offset="100%" stopColor="#a4ca73" stopOpacity="0.7" />
            </linearGradient>
          </defs>
          <circle
            cx="80"
            cy="80"
            r="72"
            fill="none"
            stroke="url(#sweep-grad)"
            strokeWidth="4"
            strokeDasharray="40 413"
            strokeLinecap="round"
          />
        </motion.svg>

        {/* Orbiting particles */}
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 rounded-full bg-fl-leaf"
            style={{
              top: "50%",
              left: "50%",
              marginTop: "-4px",
              marginLeft: "-4px",
            }}
            animate={{
              x: [
                Math.cos((i * 2 * Math.PI) / 3) * 56,
                Math.cos((i * 2 * Math.PI) / 3 + Math.PI) * 56,
                Math.cos((i * 2 * Math.PI) / 3 + 2 * Math.PI) * 56,
              ],
              y: [
                Math.sin((i * 2 * Math.PI) / 3) * 56,
                Math.sin((i * 2 * Math.PI) / 3 + Math.PI) * 56,
                Math.sin((i * 2 * Math.PI) / 3 + 2 * Math.PI) * 56,
              ],
              opacity: [0.3, 1, 0.3],
              scale: [0.6, 1.2, 0.6],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "linear",
              delay: i * 0.8,
            }}
          />
        ))}

        {/* Center pulse dot */}
        <motion.div
          className="absolute top-1/2 left-1/2 w-3 h-3 -mt-1.5 -ml-1.5 rounded-full bg-fl-leaf"
          animate={{
            scale: [1, 1.8, 1],
            opacity: [0.6, 1, 0.6],
            boxShadow: [
              "0 0 4px rgba(164,202,115,0.3)",
              "0 0 16px rgba(164,202,115,0.8)",
              "0 0 4px rgba(164,202,115,0.3)",
            ],
          }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Cycling message */}
      <div className="h-6 relative overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.p
            key={displayMessage}
            className="text-sm text-fl-forest font-light tracking-wide text-center"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            {displayMessage}
          </motion.p>
        </AnimatePresence>
      </div>

      {/* Progress dots */}
      <div className="flex gap-1.5 mt-4">
        {[0, 1, 2, 3, 4].map((i) => (
          <motion.div
            key={i}
            className="w-1.5 h-1.5 rounded-full bg-fl-evergreen"
            animate={{ opacity: [0.2, 0.8, 0.2] }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              delay: i * 0.2,
            }}
          />
        ))}
      </div>

      {/* Elapsed timer — shows after threshold */}
      <AnimatePresence>
        {elapsed > showTimerAfter && (
          <motion.p
            className="mt-4 text-xs text-fl-evergreen-40 font-light"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {formatElapsed(elapsed)} elapsed — still working
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}
